/**
 * legal-page.tsx — Ba văn bản pháp lý công khai (không cần đăng nhập):
 *   /dieu-khoan · /chinh-sach-bao-mat · /xoa-du-lieu
 * Đây là các URL khai với Meta khi xin Live app (Privacy Policy URL, Terms of Service URL, User Data Deletion).
 * Nội dung ở `legal-content.ts`, thông tin pháp nhân ở `legal-info.ts`.
 */
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { BrandLogo } from '@/components/shared/brand-logo'
import { Card } from '@/components/ui/card'
import { LEGAL_INFO } from './legal-info'
import { buildLegalDoc, LEGAL_PATH, type LegalKind } from './legal-content'

export function LegalPage({ kind }: { kind: LegalKind }) {
  const { t } = useTranslation('authPages')
  // Nhãn điều hướng chỉ là nhãn UI (menu), KHÔNG phải nội dung văn bản pháp lý (legal-content.ts giữ nguyên tiếng Việt).
  const nav: { kind: LegalKind; label: string }[] = [
    { kind: 'privacy', label: t('legal.navPrivacy') },
    { kind: 'terms', label: t('legal.navTerms') },
    { kind: 'data-deletion', label: t('legal.navDataDeletion') },
    { kind: 'claude-connector', label: t('legal.navClaudeConnector') },
  ]
  const doc = buildLegalDoc(kind, LEGAL_INFO)

  useEffect(() => {
    const prev = document.title
    document.title = `${doc.title} — ${LEGAL_INFO.product}`
    return () => {
      document.title = prev
    }
  }, [doc.title])

  const company = [LEGAL_INFO.companyName, LEGAL_INFO.companyNameEn, LEGAL_INFO.address, LEGAL_INFO.taxId && `MST: ${LEGAL_INFO.taxId}`].filter(Boolean)

  return (
    <div className="min-h-screen bg-background px-4 py-8 text-foreground">
      <div className="mx-auto max-w-3xl space-y-5">
        <div className="flex items-center justify-between gap-3">
          <BrandLogo variant="header" />
          <Link to="/login" className="text-sm text-primary hover:underline">{t('legal.login')}</Link>
        </div>

        <nav className="flex flex-wrap gap-x-4 gap-y-1 text-sm" aria-label={t('legal.navAriaLabel')}>
          {nav.map((n) => (
            <Link
              key={n.kind}
              to={LEGAL_PATH[n.kind]}
              className={n.kind === kind ? 'font-medium text-foreground' : 'text-primary hover:underline'}
              aria-current={n.kind === kind ? 'page' : undefined}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <Card className="space-y-6 p-6">
          <header className="space-y-2">
            <h1 className="text-2xl font-semibold">{doc.title}</h1>
            <p className="text-sm text-muted-foreground">{doc.lead}</p>
          </header>

          {doc.sections.map((s) => (
            <section key={s.heading} className="space-y-2">
              <h2 className="text-base font-semibold">{s.heading}</h2>
              {s.paragraphs?.map((t) => (
                <p key={t} className="text-sm leading-relaxed text-muted-foreground">{t}</p>
              ))}
              {s.bullets && (
                <ul className="list-disc space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground">
                  {s.bullets.map((t) => <li key={t}>{t}</li>)}
                </ul>
              )}
              {s.steps && (
                <ol className="list-decimal space-y-1 pl-5 text-sm leading-relaxed text-muted-foreground">
                  {s.steps.map((t) => <li key={t}>{t}</li>)}
                </ol>
              )}
            </section>
          ))}

          {company.length > 0 && (
            <footer className="space-y-0.5 border-t pt-4 text-xs text-muted-foreground">
              {company.map((l) => <p key={l}>{l}</p>)}
              {LEGAL_INFO.email && <p>{t('legal.emailLabel')}: {LEGAL_INFO.email}</p>}
              {LEGAL_INFO.phone && <p>{t('legal.phoneLabel')}: {LEGAL_INFO.phone}</p>}
            </footer>
          )}
        </Card>
      </div>
    </div>
  )
}
